import * as React from 'react'

import { cn } from '@/infra'

const SectionHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex h-16 items-center justify-between bg-neutral-200 p-4', className)} {...props} />
)

const SectionTitle = ({ className, children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3 className={cn('font-poppins text-base font-normal uppercase', className)} {...props}>
    {children}
  </h3>
)

const SectionContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('space-y-3 overflow-hidden rounded-none border border-neutral-200 px-6 py-5', className)}
    {...props}
  />
)

const SectionDescription = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-muted-foreground text-sm', className)} {...props} />
)

interface SectionGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  withBorder?: boolean
}

const SectionGroup = ({ className, withBorder, ...props }: SectionGroupProps) => (
  <div
    data-testid="section-group"
    className={cn(
      'grid grid-cols-12 items-start gap-4 sm:gap-x-4 sm:gap-y-4',
      withBorder && 'border-muted pb-3 sm:border-b',
      className,
    )}
    {...props}
  />
)

export interface SectionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string
  colSpan?: 4 | 6 | 8 | 12
  withRightBorder?: boolean
}

const SectionItem = ({ label, colSpan = 12, withRightBorder, className, children, ...props }: SectionItemProps) => {
  const colSpanClasses = {
    4: 'sm:col-span-4',
    6: 'sm:col-span-6',
    8: 'sm:col-span-8',
    12: 'sm:col-span-12',
  }

  return (
    <div
      data-testid="section-item"
      className={cn(
        'border-muted col-span-12 flex flex-col border-b pb-2',
        colSpanClasses[colSpan],
        withRightBorder && 'mb-3 sm:mb-0 sm:border-r sm:border-b-0 sm:pb-0',
        !withRightBorder && 'sm:border-b-0 sm:pb-0',
        className,
      )}
      {...props}
    >
      <p className="font-poppins mb-2 text-sm font-medium">{label}</p>
      {children}
    </div>
  )
}

interface SectionItemValueProps extends React.HTMLAttributes<HTMLParagraphElement> {
  uppercase?: boolean
}

const SectionItemValue = ({ className, uppercase, ...props }: SectionItemValueProps) => (
  <p
    data-testid="section-item-value"
    className={cn('font-poppins text-base font-normal', uppercase && 'uppercase', className)}
    {...props}
  />
)

type SectionItemListaValoresProps = {
  label: string
  valores: string[]
  colSpan?: 4 | 6 | 8 | 12
  withRightBorder?: boolean
}

export const SectionItemListaValores = ({
  label,
  valores,
  colSpan,
  withRightBorder,
}: Readonly<SectionItemListaValoresProps>) => {
  return (
    <SectionItem label={label} colSpan={colSpan} withRightBorder={withRightBorder}>
      <div className="space-y-1">
        {valores.map((v) => (
          <SectionItemValue key={v}>{v}</SectionItemValue>
        ))}
      </div>
    </SectionItem>
  )
}

export { SectionHeader, SectionTitle, SectionDescription, SectionContent, SectionGroup, SectionItem, SectionItemValue }
export { SectionItemParcela, SectionItemGrupoParcela } from './section-parcela'
